// Treasury App Types

export interface Account {
  id: string;
  company_id: string;
  
  // Basic Info
  name: string;
  account_number?: string;
  type: 'bank' | 'ccp' | 'cash' | 'mobile_money';
  
  // Bank Details
  bank_name?: string;
  branch?: string;
  iban?: string;
  swift?: string;
  
  // CCP specific (Algerian postal checking account)
  ccp_number?: string; // 12 digits
  ccp_key?: string; // 2 digits
  
  // Mobile Money
  mobile_provider?: 'baridi_mob' | 'cib' | 'satim' | 'other';
  mobile_number?: string;
  
  // Balance
  initial_balance: number;
  current_balance: number;
  
  // Settings
  currency: string;
  is_default: boolean;
  is_active: boolean;
  
  // Metadata
  notes?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface Transaction {
  id: string;
  company_id: string;
  account_id: string;
  
  // Transaction Info
  transaction_number: string;
  type: 'income' | 'expense' | 'transfer';
  category?: string;
  
  // Amount
  amount: number;
  currency: string;
  
  // Payment Method
  payment_method: 'cash' | 'check' | 'transfer' | 'card' | 'mobile' | 'ccp';
  
  // Date
  transaction_date: string;
  value_date?: string; // For checks
  
  // References
  reference_type?: 'invoice' | 'bill' | 'payroll' | 'other';
  reference_id?: string;
  
  // Check Details (Algerian checks valid for 180 days)
  check_number?: string;
  check_date?: string;
  check_bank?: string;
  check_status?: 'pending' | 'cleared' | 'bounced' | 'cancelled';
  check_drawer_name?: string;
  
  // Contact
  contact_type?: 'client' | 'supplier' | 'employee' | 'other';
  contact_id?: string;
  contact_name?: string;
  
  // Description
  description: string;
  notes?: string;
  
  // Reconciliation
  is_reconciled: boolean;
  reconciliation_date?: string;
  
  // Metadata
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface Transfer {
  id: string;
  company_id: string;
  
  transfer_number: string;
  from_account_id: string;
  to_account_id: string;
  
  amount: number;
  transfer_fee: number;
  transfer_date: string;
  
  description: string;
  notes?: string;
  
  // Related transactions
  debit_transaction_id?: string;
  credit_transaction_id?: string;
  fee_transaction_id?: string;
  
  status: 'pending' | 'completed' | 'cancelled';
  
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Algerian Banks - Complete List (2024)
export const ALGERIAN_BANKS = [
  // Public Banks
  'BNA - Banque Nationale d\'Algérie',
  'BEA - Banque Extérieure d\'Algérie',
  'CPA - Crédit Populaire d\'Algérie',
  'BADR - Banque de l\'Agriculture et du Développement Rural',
  'BDL - Banque de Développement Local',
  'CNEP - Caisse Nationale d\'Épargne et de Prévoyance',
  
  // Islamic Banks
  'Al Baraka Bank Algeria',
  'Al Salam Bank Algeria',
  
  // Private Banks
  'ABC - Arab Banking Corporation',
  'Housing Bank for Trade and Finance Algeria',
  'Natixis Algérie',
  'Société Générale Algérie',
  'BNP Paribas El Djazair',
  'Trust Bank Algeria',
  'Gulf Bank Algeria',
  'Fransabank El Djazair',
  'HSBC Algérie',
  'Citibank N.A. Algeria',
  'AGB - Algerian Gulf Bank',
  'Bank Al Wafa',
  'Calyon Algérie (Crédit Agricole)',
  
  // Other
  'Autre banque',
] as const;

// Transaction Categories
export const TRANSACTION_CATEGORIES = {
  income: [
    'sale', // Vente
    'service', // Service rendu
    'refund', // Remboursement reçu
    'interest', // Intérêts bancaires
    'loan_received', // Prêt reçu
    'investment', // Investissement
    'other_income', // Autre revenu
  ],
  expense: [
    'purchase', // Achat de marchandises
    'salary', // Salaire
    'rent', // Loyer
    'utilities', // Services publics (eau, électricité, gaz)
    'tax', // Impôts et taxes
    'insurance', // Assurance
    'maintenance', // Entretien et réparations
    'marketing', // Marketing et publicité
    'transport', // Transport et carburant
    'supplies', // Fournitures de bureau
    'bank_fees', // Frais bancaires
    'loan_payment', // Remboursement de prêt
    'other_expense', // Autre dépense
  ],
} as const;

// Category Labels (for display)
export const CATEGORY_LABELS: Record<string, { fr: string; ar: string }> = {
  // Income
  sale: { fr: 'Vente', ar: 'بيع' },
  service: { fr: 'Service rendu', ar: 'خدمة مقدمة' },
  refund: { fr: 'Remboursement reçu', ar: 'استرجاع' },
  interest: { fr: 'Intérêts bancaires', ar: 'فوائد بنكية' },
  loan_received: { fr: 'Prêt reçu', ar: 'قرض مستلم' },
  investment: { fr: 'Investissement', ar: 'استثمار' },
  other_income: { fr: 'Autre revenu', ar: 'دخل آخر' },
  
  // Expense
  purchase: { fr: 'Achat marchandises', ar: 'شراء بضائع' },
  salary: { fr: 'Salaire', ar: 'راتب' },
  rent: { fr: 'Loyer', ar: 'إيجار' },
  utilities: { fr: 'Services publics', ar: 'خدمات عامة' },
  tax: { fr: 'Impôts et taxes', ar: 'ضرائب ورسوم' },
  insurance: { fr: 'Assurance', ar: 'تأمين' },
  maintenance: { fr: 'Entretien', ar: 'صيانة' },
  marketing: { fr: 'Marketing', ar: 'تسويق' },
  transport: { fr: 'Transport', ar: 'نقل' },
  supplies: { fr: 'Fournitures', ar: 'لوازم' },
  bank_fees: { fr: 'Frais bancaires', ar: 'رسوم بنكية' },
  loan_payment: { fr: 'Remboursement prêt', ar: 'تسديد قرض' },
  other_expense: { fr: 'Autre dépense', ar: 'مصروف آخر' },
  
  // Transfer (internal)
  transfer: { fr: 'Transfert interne', ar: 'تحويل داخلي' },
};

// Mobile Money Providers
export const MOBILE_PROVIDERS = [
  { value: 'baridi_mob', label: 'Baridi Mob', icon: '📱' },
  { value: 'cib', label: 'CIB', icon: '💳' },
  { value: 'satim', label: 'SATIM', icon: '💳' },
  { value: 'other', label: 'Autre', icon: '📱' },
] as const;

// Account Type Icons & Colors
export const ACCOUNT_TYPES = {
  bank: {
    label: 'Compte Bancaire',
    labelAr: 'حساب بنكي',
    icon: '🏦',
    color: 'blue',
  },
  ccp: {
    label: 'CCP',
    labelAr: 'حساب جاري بريدي',
    icon: '📮',
    color: 'green',
  },
  cash: {
    label: 'Caisse',
    labelAr: 'صندوق',
    icon: '💵',
    color: 'purple',
  },
  mobile_money: {
    label: 'Mobile Money',
    labelAr: 'محفظة إلكترونية',
    icon: '📱',
    color: 'orange',
  },
} as const;

export type AccountType = keyof typeof ACCOUNT_TYPES;
export type TransactionType = Transaction['type'];
export type PaymentMethod = Transaction['payment_method'];
export type CheckStatus = NonNullable<Transaction['check_status']>;
